import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, Globe, CheckCircle, AlertCircle, Loader2, AlertTriangle, Activity, Info } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { getBotConfigSync, classifyDomain, extractDomain } from '@/lib/botConfig';

export default function ScanPage() {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('queued');
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  const [url, setUrl] = useState('');

  const progressSteps = [
    { id: 'init', label: 'Initializing scan', minProgress: 0 },
    { id: 'loading', label: 'Loading website', minProgress: 20 },
    { id: 'collecting', label: 'Collecting network requests', minProgress: 50 },
    { id: 'analyzing', label: 'Analyzing third-party agents', minProgress: 70 },
    { id: 'scoring', label: 'Calculating Agent Pressure Score', minProgress: 90 },
    { id: 'complete', label: 'Scan complete', minProgress: 100 }
  ];

  useEffect(() => {
    let pollInterval;
    let progressInterval;
    let logIndex = 0;

    const addLog = (message, type = 'info') => {
      setLogs(prev => [...prev, { message, type, timestamp: new Date().toISOString() }]);
    };

    const simulateLogs = () => {
      const scanLogs = [
        { msg: '> Initializing headless browser...', type: 'info' },
        { msg: '> Browser instance created', type: 'success' },
        { msg: '> Navigating to target URL...', type: 'info' },
        { msg: '> Capturing network requests...', type: 'info' },
        { msg: '> Detected third-party domains', type: 'highlight' },
        { msg: '> Mapping domains to companies...', type: 'info' },
        { msg: '> Categorizing trackers...', type: 'info' },
        { msg: '> Computing APS score...', type: 'info' },
      ];

      if (logIndex < scanLogs.length) {
        addLog(scanLogs[logIndex].msg, scanLogs[logIndex].type);
        logIndex++;
      }
    };

    const pollStatus = async () => {
      try {
        const result = await api.getScanStatus(scanId);
        setStatus(result.status);
        setUrl(result.url);

        if (result.status === 'running') {
          // Simulate progress
          setProgress(prev => Math.min(prev + 5, 85));
          simulateLogs();
        } else if (result.status === 'done') {
          setProgress(100);
          addLog('> Scan completed successfully!', 'success');
          clearInterval(pollInterval);
          clearInterval(progressInterval);
          
          // Navigate to report after brief delay
          setTimeout(() => {
            if (result.public_report_id) {
              navigate(`/r/${result.public_report_id}`);
            }
          }, 1000);
        } else if (result.status === 'failed') {
          setError(result.error || 'Scan failed');
          addLog(`> Error: ${result.error || 'Unknown error'}`, 'error');
          clearInterval(pollInterval);
          clearInterval(progressInterval);
        }
      } catch (err) {
        console.error('Poll error:', err);
        if (err.response?.status === 404) {
          setError('Scan not found');
          clearInterval(pollInterval);
          clearInterval(progressInterval);
        }
      }
    };

    // Initial log
    addLog('> Starting AgentCheck scan...', 'info');
    setProgress(5);

    // Start polling
    pollInterval = setInterval(pollStatus, 2000);
    progressInterval = setInterval(() => {
      if (status === 'running' || status === 'queued') {
        simulateLogs();
      }
    }, 3000);

    // Initial poll
    pollStatus();

    return () => {
      clearInterval(pollInterval);
      clearInterval(progressInterval);
    };
  }, [scanId, navigate, status]);

  const currentStep = progressSteps.find(step => progress <= step.minProgress) || progressSteps[progressSteps.length - 1];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#007AFF] flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <span 
              className="text-xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              AgentCheck
            </span>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full" data-testid="scan-progress-container">
          {/* URL being scanned */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 text-sm text-slate-600 mb-4">
              <Globe className="w-4 h-4" />
              <span className="font-mono">{url || 'Loading...'}</span>
            </div>
            <h1 
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {error ? 'Scan Failed' : status === 'done' ? 'Scan Complete' : 'Scanning Website'}
            </h1>
          </div>

          {/* Progress visualization */}
          <div className="mb-12">
            {/* Animated scanner */}
            <div className="relative w-48 h-48 mx-auto mb-8">
              {/* Outer ring */}
              <div className="absolute inset-0 rounded-full border-4 border-slate-100" />
              
              {/* Progress ring */}
              <svg className="absolute inset-0 w-full h-full -rotate-90">
                <circle
                  cx="96"
                  cy="96"
                  r="90"
                  fill="none"
                  stroke={error ? '#EF4444' : '#007AFF'}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${progress * 5.65} 565`}
                  className="transition-all duration-500"
                />
              </svg>

              {/* Center content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                {error ? (
                  <AlertCircle className="w-12 h-12 text-red-500 mb-2" />
                ) : status === 'done' ? (
                  <CheckCircle className="w-12 h-12 text-emerald-500 mb-2" />
                ) : (
                  <Loader2 className="w-12 h-12 text-[#007AFF] animate-spin mb-2" />
                )}
                <span 
                  className="text-3xl font-bold text-slate-900"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  data-testid="progress-percentage"
                >
                  {progress}%
                </span>
              </div>

              {/* Pulse animation */}
              {!error && status !== 'done' && (
                <>
                  <div className="absolute inset-0 rounded-full bg-blue-500/10 animate-pulse-ring" />
                  <div className="absolute inset-0 rounded-full bg-blue-500/5 animate-pulse-ring" style={{ animationDelay: '0.5s' }} />
                </>
              )}
            </div>

            {/* Progress steps */}
            <div className="flex items-center justify-center gap-2 mb-8">
              {progressSteps.slice(0, -1).map((step, index) => (
                <div
                  key={step.id}
                  className={`w-2 h-2 rounded-full transition-all ${
                    progress >= step.minProgress 
                      ? error ? 'bg-red-500' : 'bg-[#007AFF]' 
                      : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>

            {/* Current status */}
            <p className="text-center text-slate-600" data-testid="scan-status-text">
              {error || currentStep.label}
            </p>
          </div>

          {/* Terminal output */}
          {url && (() => {
            const domain = extractDomain(url);
            const check = domain ? classifyDomain(domain, getBotConfigSync()) : { tier: null };
            return check.tier && !error && status !== 'done' ? (
              <div className={`mb-6 flex items-start gap-2.5 px-5 py-3 rounded-xl border ${
                check.tier === 'A' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'
              }`} data-testid="scan-protected-warning" data-tier={check.tier}>
                {check.tier === 'A'
                  ? <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  : <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                }
                <div className="flex-1">
                  <p className={`text-sm font-medium ${check.tier === 'A' ? 'text-amber-800' : 'text-blue-800'}`}>
                    {check.tier === 'A' ? 'This site often blocks automated scans. Partial results are common.' : 'This site may limit automated scans. Results can vary.'}
                  </p>
                  <button className="inline-flex items-center gap-1 text-xs text-[#007AFF] font-medium mt-1 hover:underline" onClick={() => navigate('/monitor')}>
                    <Activity className="w-3 h-3" /> Install Monitor for full results
                  </button>
                </div>
              </div>
            ) : null;
          })()}
          <div className="bg-slate-900 rounded-xl p-6 shadow-lg" data-testid="terminal-output">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="ml-2 text-xs text-slate-500 font-mono">agentcheck-scanner</span>
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto terminal-text">
              {logs.map((log, index) => (
                <div 
                  key={index}
                  className={`font-mono text-sm ${
                    log.type === 'success' ? 'text-emerald-400' :
                    log.type === 'error' ? 'text-red-400' :
                    log.type === 'highlight' ? 'text-[#007AFF]' :
                    'text-slate-400'
                  }`}
                >
                  {log.message}
                </div>
              ))}
              {!error && status !== 'done' && (
                <span className="inline-block w-2 h-4 bg-white animate-terminal-cursor" />
              )}
            </div>
          </div>

          {/* Error action */}
          {error && (
            <div className="mt-8 text-center">
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 rounded-full bg-[#007AFF] text-white font-semibold hover:bg-[#0062CC] transition-colors"
                data-testid="try-again-button"
              >
                Try Another URL
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
